/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { dwService } from 'amb/services/dw.service';
import { bannerCarousel } from '../helpers/banner-carousel.helper';

const serverOptions = dwService.preference('video')?.videoOptions || {};

/**
 * @typedef {import('../models/video/abstract-video').AbstractVideo} AbstractVideo
 * @typedef {import('../models/video/metadata').Metadata} Metadata
 * @typedef {import('swiper').Swiper} Swiper
 *
 * Video options container
 *
 * Available presets:
 *
 *  - `default` Applied to all videos
 *  - `readonly` - Used for video which has no controls, the video playback will controlled programmatically
 *  - `slick_bridge` - Used for video inside `amb-slick` directive
 *    - If slick `autoplay` enabled, this preset will pause slick slider when video start playing.
 *    - It also resume slick slider playback when video paused or ended.
 *  - `swiper_bridge` - Used for video inside `amb-swiper` / banner carousel
 *    - If swiper `autoplay` enabled, pauses swiper when video starts playing.
 *    - On ended, advances to the next slide and resumes autoplay.
 *    - Auto-applied for videos inside `.banner--carousel`.
 */
export const videoOptions = {
  // Extends new options exposed from server
  ...serverOptions,

  /**
   * Default video configurations, always applied.
   *
   * @param {object}      params
   * @param {HTMLElement} params.element
   */
  default({ element }) {
    const options = {
      // Vimeo
      autopause: false,
      byline: false,
      height: 1080,
      portrait: false,
      title: false,
      transparent: true,

      // Youtube
      autohide: true,
      disablekb: true,
      modestbranding: true,
      rel: false,
      showinfo: false,

      // Custom Options
      size: 'cover',

      // By default, all videos will rendered as `readonly` content
      readonly: true,

      // When enabled, video playback will control programmatically via `amb-intersection` directive with `video` preset.
      autopilot: true,

      // Extends `default` preset exposed from server if exists
      ...(serverOptions.default || {}),

      // Trigger default `metadataLoaded` callback
      onMetaLoaded: this.onMetadataLoaded
    };

    // Auto-apply swiper bridge for hero banner videos (no PD preset required)
    if (element.closest('.banner--carousel')) {
      Object.assign(options, this.swiper_bridge({ element }));
    }

    return options;
  },

  /**
   * Contains isolated options which will apply when enable `autopilot` option.
   * Options belong this preset can't be overridden by server options or other presets.
   *
   * `autopilot` option will auto turned on if video rendered as `readonly`.
   *
   * @readonly Do not extends or override this preset
   */
  autopilot: {
    // The `lazy` flag prevent initialize video player when create new video instance
    // The initialization will trigger by directive which controls video playback.
    lazy: true
  },

  /**
   * Readonly preset usually used for video which has no controls, such as: background video, product tile video, etc..
   * When enabled, `autopilot` mode will force to enable as well to control the video playback as video rendered as `readonly`.
   *
   * **Note:**
   *  - Options belong this preset can't be overridden by server options or other presets, except `autopilot` preset.
   *  - `readonly` options will auto applied to video if `readonly` attribute exists on video element.
   *
   * @readonly Do not extends or override this preset
   * @example
   *
   * ```html
   *  <!-- without `readonly` attribute -->
   *  <div amb-video="16401445" data-options="readonly"></div>
   *
   *  <!-- or with `readonly` attribute -->
   *  <div amb-video="16401445" readonly></div>
   * ```
   */
  readonly: {
    controls: false
  },

  /**
   * Car features preset used to control video playback when user interact with the video.
   *
   * Desktop: Video plays on hover, pauses on leave, toggles on click
   * Mobile: Video auto plays when entering viewport, pauses when leaving viewport
   *
   * @param {object}      args
   * @param {HTMLElement} args.element
   *
   * @returns {object}
   */
  car_features({element}) {
    /**
     * @type {AbstractVideo['player']}
     */
    let player;

    // Detect if device has fine pointer capability (mouse/trackpad)
    // Using 'any-pointer' to catch hybrid devices where mouse is available
    const hasFinePointer = window.matchMedia('(any-pointer: fine)').matches;

    // Only add mouse events if fine pointer is available
    if (hasFinePointer) {
      $(element).on('mouseenter mouseleave click', (event) => {
        if (event.type === 'mouseenter') {
          player?.play();
        } else if (event.type === 'mouseleave') {
          player?.pause();
        } else if (event.type === 'click') {
          player?.toggle();
        }
      });
    }

    return {
      // Fine pointer (mouse/trackpad): use 'init_video' preset, mouse events control playback
      // Coarse pointer (touch): use 'video' preset for intersection observer auto play/pause
      autopilot: hasFinePointer ? 'init_video' : 'video',
      ratio: 0.75,
      readonly: true,

      /**
       * @param {AbstractVideo} video
       */
      onReady(video) {
        player = video.player;
      },
    }
  },

  /**
   * Slick bridge preset used for video inside slick carousel directive.
   * This preset communicate with slick directive to control interactive between 2 components.
   *
   * If current slick directive enabled `autoplay`. This preset will:
   *  - Disable `loop` for current video because `ended` event won't trigger at the end of video if `loop = true`.
   *  - Pause slick slider when video start playing..
   *  - When current video `pause` or `ended`, move to next slide and resume slick `autoplay`
   *
   * @param {object}      params
   * @param {HTMLElement} params.element
   */
  slick_bridge({ element }) {
    const $slick = $(element).closest('.slick-slider');
    const autoplay = $slick.slick('slickGetOption', 'autoplay');
    const autoplayOptions = autoplay ? this.registerSlickVideoEvents($slick, ...arguments) : {};

    return {
      /**
       * Lazy flag used to prevent video auto init.
       * Once video rendered inside a slick carousel, we only init when slide which hold this video active.
       */
      lazy: true,

      // Extends `slick_bridge` preset exposed from server if exists
      ...(serverOptions.slick_bridge || {}),

      ...autoplayOptions
    };
  },

  /**
   * This helper method register some video events to control the `autoplay` behavior of slick wrapper.
   *
   * @protected
   *
   * @param {JQuery} $slick jQuery slick element
   */
  registerSlickVideoEvents($slick) {
    return {
      loop: false,

      // Youtube callback
      onStateChange: (video, { data }) => this.onSlickVideoStateChange(video, data, $slick),

      // Vimeo callbacks
      bufferstart: (video) => this.onSlickVideoStateChange(video, 'bufferstart', $slick),
      play: (video) => this.onSlickVideoStateChange(video, 'play', $slick),
      pause: (video) => this.onSlickVideoStateChange(video, 'pause', $slick),
      ended: (video) => this.onSlickVideoStateChange(video, 'ended', $slick)
    };
  },

  /**
   * Handle slick `autoplay` state when any video state change
   *
   * @protected
   *
   * @param {AbstractVideo} video  Video wrapper isntance
   * @param {string}        state  Current video state
   * @param {JQuery}        $slick jQuery slick element
   */
  onSlickVideoStateChange(video, state, $slick) {
    const slick = $slick.get(0).slick;
    const uniqueKey = `${video.element.ambid}:${video.id}`;

    if (!slick.videos) {
      slick.videos = new Set();
    }

    /**
     * List of unique playing videos
     *
     * @type {Set<string>}
     */
    const videos = slick.videos;

    /* global YT */
    switch (state) {
      case 'bufferstart': // vimeo
      case 'play': // vimeo
      case YT?.PlayerState?.BUFFERING: // youtube
      case YT?.PlayerState?.PLAYING: // youtube
        videos.add(uniqueKey); break;

      case 'pause': // vimeo
      case 'ended': // vimeo
      case YT?.PlayerState?.PAUSED: // youtube
      case YT?.PlayerState?.ENDED: // youtube
        videos.delete(uniqueKey); break;
    }

    if (videos.size === 0) {
      if (state === 'ended' || state === YT?.PlayerState?.ENDED) {
        $slick.slick('slickNext');
      }

      $slick.slick('slickPlay');
    } else {
      $slick.slick('slickPause');
    }
  },

  /**
   * Swiper bridge for video inside the banner carousel.
   *
   * Muted (browser autoplay policy). `autopilot` off — banner sync owns play/pause.
   * Event wiring lives in `bannerCarousel`.
   *
   * @param {object}      params
   * @param {HTMLElement} params.element
   */
  swiper_bridge({ element }) {
    const swiper = this.resolveSwiper(element);
    const autoplayEnabled = swiper?.params?.autoplay
      && swiper.params.autoplay.enabled !== false;
    const autoplayOptions = autoplayEnabled ? bannerCarousel.registerVideoEvents(swiper) : {};

    return {
      lazy: true,
      mute: true,
      muted: true,

      ...(serverOptions.swiper_bridge || {}),

      autopilot: false,

      ...autoplayOptions
    };
  },

  /**
   * @protected
   *
   * @param {HTMLElement} element
   *
   * @returns {Swiper|null}
   */
  resolveSwiper(element) {
    const swiperEl = element.closest('.swiper')
      || element.closest('.banner--carousel')?.querySelector('[amb-swiper], .swiper');

    return swiperEl?.swiper || null;
  },

  /**
   * Fires when video metadata loaded to initialize video thumbnails
   *
   * @protected
   *
   * @param {AbstractVideo} video    The video wrapper instance
   * @param {Metadata}      metadata The metadata adapter
   */
  onMetadataLoaded(video, metadata) {
    if (!metadata.thumbnail) {
      return;
    }

    if (!video.element.querySelector(`img[data-thumbnail="${metadata.id}"]`)) {
      const div = document.createElement('div');
      div.innerHTML = `<img data-thumbnail="${metadata.id}" width="100%" height="auto">`;

      video.element.appendChild(div.firstChild);
    }

    document
      .querySelectorAll(`[data-thumbnail="${metadata.id}"]`)
      .forEach(thumbnail => {
        const parent = thumbnail.parentElement;

        if (!thumbnail.hasAttribute('src')) {
          thumbnail.setAttribute('src', metadata.thumbnail);
        }

        if (parent.classList.contains('aspect-ratio')) {
          video.applyClip(thumbnail);

          if (!parent.hasAttribute('amb-video') && !parent.dataset.ambVideo) {
            parent.style.setProperty('padding-bottom', `${video.position.percentageHeight}%`);
          }
        }
      })
    ;
  },

  hero_banner: {
    ratio: 3,
    responsive: {
      599: {
        ratio: 0.75 // >= 599px
      },
      1023: {
        ratio: 1.78 // >= 1023px
      },
      1919: {
        ratio: 2.33 // >= 1919px
      }
    }
  }
};
